import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog} from '../Dialog'
import {Button} from '../Button'
import {Input} from '../Input'
import {AgencyRepository} from '../../repositories/AgencyRepository.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {CreateAgencyUserWithPasswordUseCase} from '../../domain/useCases/agencies/CreateAgencyUserWithPasswordUseCase.ts'
import {CreateAgencyUserWithPhoneUseCase} from '../../domain/useCases/agencies/CreateAgencyUserWithPhoneUseCase.ts'
import {GenerateAgencyUserMagicLinkUseCase} from '../../domain/useCases/agencies/GenerateAgencyUserMagicLinkUseCase.ts'
import {LinkAgencyUserPasswordUseCase} from '../../domain/useCases/agencies/LinkAgencyUserPasswordUseCase.ts'
import {LinkAgencyUserPhoneUseCase} from '../../domain/useCases/agencies/LinkAgencyUserPhoneUseCase.ts'
import {LinkAgencyUserMagicLinkUseCase} from '../../domain/useCases/agencies/LinkAgencyUserMagicLinkUseCase.ts'
import {ALL_AGENCY_USER_AUTH_METHODS, AgencyUser, AgencyUserAuthMethod} from '../../domain/entities/AgencyUser.ts'
import {parseBackendError} from '../../utils/parseBackendError.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconCopy from '../../assets/icons/copy.svg?react'
import IconCheck from '../../assets/icons/check.svg?react'

const agencyRepo = new AgencyRepository(new AuthenticationRepository())
const createPasswordUseCase = new CreateAgencyUserWithPasswordUseCase(agencyRepo)
const createPhoneUseCase = new CreateAgencyUserWithPhoneUseCase(agencyRepo)
const generateMagicLinkUseCase = new GenerateAgencyUserMagicLinkUseCase(agencyRepo)
const linkPasswordUseCase = new LinkAgencyUserPasswordUseCase(agencyRepo)
const linkPhoneUseCase = new LinkAgencyUserPhoneUseCase(agencyRepo)
const linkMagicLinkUseCase = new LinkAgencyUserMagicLinkUseCase(agencyRepo)

const METHOD_LABEL_KEY: Record<AgencyUserAuthMethod, string> = {
    password: 'agencyUsers.methodPassword',
    phone: 'agencyUsers.methodPhone',
    magicLink: 'agencyUsers.methodMagicLink',
}

// Fixed post-login redirect for every agency-user magic link — must be in
// Firebase's authorized domains list, so this isn't left up to free text.
const MAGIC_LINK_CONTINUE_URL = 'https://agencias.51trips.com/auth/login'

// magicLink stays available even once the user already has it: unlike
// password/phone, admins need to be able to regenerate/resend it as many
// times as needed (e.g. the previous link expired or got lost).
export function availableMethodsFor(existingUser: AgencyUser | null | undefined): AgencyUserAuthMethod[] {
    return existingUser
        ? ALL_AGENCY_USER_AUTH_METHODS.filter((m) => m === 'magicLink' || !existingUser.authMethods.includes(m))
        : ALL_AGENCY_USER_AUTH_METHODS
}

interface CreateAgencyUserDialogProps {
    agencyId: string
    // When set, the dialog attaches an ADDITIONAL method onto this existing
    // agency user (same uid) instead of creating a brand new one — set it via
    // props right before calling .open(), same pattern as the row-scoped
    // dialogs in PackActivityTables.
    existingUser?: AgencyUser | null
    // Fired once a user is actually created or linked — the caller should
    // refetch the list. Never fired for a validation/permission failure.
    onCreated: () => void
}

// One dialog, two modes: create a brand new agency user (three flows — the
// API's /users/password, /users/phone, /users/magic-link), or link an
// additional method onto an existing one (/users/{uid}/*). Firebase ties
// password and magic-link to the same `email`, so linking either onto a user
// that already has an email requires reusing that exact email.
export const CreateAgencyUserDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, CreateAgencyUserDialogProps>(
    ({agencyId, existingUser, onCreated}, ref) => {
        const {t} = useTranslation('dashboard')
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
        // Bumped by every open() — the effect below depends on it too, so the
        // form resets on every open even when existingUser hasn't changed
        // (e.g. opening "Añadir usuario" twice in a row).
        const [openToken, setOpenToken] = useState(0)

        const availableMethods = availableMethodsFor(existingUser)

        const [method, setMethod] = useState<AgencyUserAuthMethod>('password')
        const [email, setEmail] = useState('')
        const [password, setPassword] = useState('')
        const [phoneNumber, setPhoneNumber] = useState('')
        const [displayName, setDisplayName] = useState('')

        const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
        const [generalError, setGeneralError] = useState<string | null>(null)
        const [isSubmitting, setIsSubmitting] = useState(false)

        // Set once a magic link has been generated — swaps the form for the
        // share-this-link view instead of closing. The backend already
        // emails it via Brevo; this is just a manual-share fallback.
        const [generatedLink, setGeneratedLink] = useState<string | null>(null)
        const [linkCopied, setLinkCopied] = useState(false)

        // Resets the form whenever the dialog is (re)opened. Deliberately NOT
        // done inside the imperative open() below: the caller sets
        // existingUser via a state update (setLinkingUser) and calls
        // ref.open() in the same synchronous handler, so open() would still
        // see the PREVIOUS existingUser prop — React hasn't re-rendered yet.
        // Reacting to the prop change instead (plus openToken, so re-opening
        // with the same existingUser — e.g. "Añadir usuario" twice — still
        // resets) always reads the current value.
        useEffect(() => {
            const methods = availableMethodsFor(existingUser)
            setMethod(methods[0] ?? 'password')
            setEmail(existingUser?.email ?? '')
            setPassword('')
            setPhoneNumber('')
            setDisplayName(existingUser?.displayName ?? '')
            setFieldErrors({})
            setGeneralError(null)
            setGeneratedLink(null)
            setLinkCopied(false)
        }, [existingUser, openToken])

        useImperativeHandle(ref, () => ({
            open: () => {
                setOpenToken((t) => t + 1)
                return dialog.current!.open()
            },
            close: dialog.current!.close
        }), [])

        const switchMethod = (next: AgencyUserAuthMethod) => {
            setMethod(next)
            setFieldErrors({})
            setGeneralError(null)
        }

        // Password/magic-link share Firebase's `email` field — once the user
        // has one on file, linking either must reuse it, so lock the input.
        const emailLocked = !!existingUser?.email && (method === 'password' || method === 'magicLink')

        const isValid = method === 'password' ? !!email.trim() && !!password
            : method === 'phone' ? !!phoneNumber.trim()
            : !!email.trim()

        const submit = async () => {
            if (!isValid || isSubmitting) return
            setFieldErrors({})
            setGeneralError(null)
            setIsSubmitting(true)
            const trimmedDisplayName = displayName.trim() || undefined

            try {
                if (method === 'password') {
                    const passwordRequest = {email: email.trim(), password, displayName: trimmedDisplayName}
                    if (existingUser) await linkPasswordUseCase.execute(agencyId, existingUser.uid, passwordRequest)
                    else await createPasswordUseCase.execute(agencyId, passwordRequest)
                    onCreated()
                    dialog.current!.close()
                } else if (method === 'phone') {
                    const phoneRequest = {phoneNumber: phoneNumber.trim(), displayName: trimmedDisplayName}
                    if (existingUser) await linkPhoneUseCase.execute(agencyId, existingUser.uid, phoneRequest)
                    else await createPhoneUseCase.execute(agencyId, phoneRequest)
                    onCreated()
                    dialog.current!.close()
                } else {
                    const magicLinkRequest = {email: email.trim(), displayName: trimmedDisplayName, continueUrl: MAGIC_LINK_CONTINUE_URL}
                    const link = existingUser
                        ? await linkMagicLinkUseCase.execute(agencyId, existingUser.uid, magicLinkRequest)
                        : await generateMagicLinkUseCase.execute(agencyId, magicLinkRequest)
                    onCreated()
                    setGeneratedLink(link)
                }
            } catch (err) {
                const parsed = parseBackendError(err, t('agencyUsers.genericErrorFallback'))
                if (parsed.fieldErrors) setFieldErrors(parsed.fieldErrors)
                else setGeneralError(parsed.message)
            } finally {
                setIsSubmitting(false)
            }
        }

        const copyLink = () => {
            if (!generatedLink) return
            navigator.clipboard.writeText(generatedLink).then(() => {
                setLinkCopied(true)
                setTimeout(() => setLinkCopied(false), 1600)
            })
        }

        return (
            <Dialog
                ref={dialog}
                title={existingUser ? t('agencyUsers.linkDialogTitle') : t('agencyUsers.dialogTitle')}
                width="mid-wide"
                withHeaderDelimiter
                headerButtons={generatedLink ? [] : [
                    <Button key="submit" variant="primary" size="small" className="max-h-[32px]"
                            disabled={!isValid || isSubmitting} onClick={submit}>
                        {isSubmitting
                            ? <IconSpinner className="size-4"/>
                            : method === 'magicLink' ? t('agencyUsers.generateLinkSubmit')
                                : existingUser ? t('agencyUsers.linkSubmit') : t('agencyUsers.createSubmit')}
                    </Button>,
                    <Button key="close" variant="secondary" size="small" className="max-h-[32px]"
                            onClick={() => dialog.current!.close()}>
                        {t('cancel')}
                    </Button>,
                ]}
            >
                {generatedLink ? (
                    <div className="flex flex-col gap-4 max-w-[460px]">
                        <span className="text-sm font-medium text-neutralBlack-900">{t('agencyUsers.linkGeneratedTitle')}</span>
                        <p className="text-[13px] text-neutralBlack-500">{t('agencyUsers.linkGeneratedHelp')}</p>
                        <div className="flex items-center gap-2 bg-neutralBlack-50 border border-neutralBlack-200 rounded-xl px-3.5 py-2.5">
                            <span className="flex-1 min-w-0 text-[13px] text-neutralBlack-800 truncate select-all">{generatedLink}</span>
                            <button type="button" onClick={copyLink}
                                    className="w-7 h-7 flex-none flex items-center justify-center rounded-full text-neutralBlack-500 hover:bg-neutralBlack-200 hover:text-neutralBlack-900">
                                {linkCopied ? <IconCheck className="size-3.5 text-success"/> : <IconCopy className="size-3.5"/>}
                            </button>
                        </div>
                        <Button variant="primary" size="small" className="max-h-[36px] self-start"
                                onClick={() => dialog.current!.close()}>
                            {t('agencyUsers.done')}
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 max-w-[420px]">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs text-neutralBlack-500">{t('agencyUsers.methodLabel')}</span>
                            <div className="flex gap-2">
                                {availableMethods.map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => switchMethod(m)}
                                        className={`h-9 px-4 rounded-full text-sm font-medium border ${
                                            method === m
                                                ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                                : 'bg-white border-neutralBlack-200 text-neutralBlack-800 hover:bg-neutralBlack-50'
                                        }`}
                                    >
                                        {t(METHOD_LABEL_KEY[m])}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {(method === 'password' || method === 'magicLink') && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('agencyUsers.fieldEmail')}</span>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                       disabled={emailLocked}
                                       invalid={!!fieldErrors.email} placeholder="nombre@agencia.com" className="h-10" block/>
                                {fieldErrors.email
                                    ? <span className="text-xs text-danger">{fieldErrors.email}</span>
                                    : emailLocked && <span className="text-xs text-neutralBlack-400">{t('agencyUsers.emailMustMatchHelp')}</span>}
                            </div>
                        )}

                        {method === 'password' && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('agencyUsers.fieldPassword')}</span>
                                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                       invalid={!!fieldErrors.password} className="h-10" block/>
                                {fieldErrors.password && <span className="text-xs text-danger">{fieldErrors.password}</span>}
                            </div>
                        )}

                        {method === 'phone' && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-xs text-neutralBlack-500">{t('agencyUsers.fieldPhone')}</span>
                                <Input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                                       invalid={!!fieldErrors.phoneNumber} placeholder="+34600000000" className="h-10" block/>
                                {fieldErrors.phoneNumber
                                    ? <span className="text-xs text-danger">{fieldErrors.phoneNumber}</span>
                                    : <span className="text-xs text-neutralBlack-400">{t('agencyUsers.phoneFormatHelp')}</span>}
                            </div>
                        )}

                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs text-neutralBlack-500">{t('agencyUsers.fieldDisplayName')}</span>
                            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                                   invalid={!!fieldErrors.displayName} className="h-10" block/>
                            {fieldErrors.displayName && <span className="text-xs text-danger">{fieldErrors.displayName}</span>}
                        </div>

                        {generalError && (
                            <div className="flex items-center gap-2.5 bg-danger/10 border border-danger/20 rounded-xl px-3.5 py-2.5">
                                <span className="text-[12.5px] leading-relaxed text-danger">{generalError}</span>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        )
    }
)
